import { useState } from "react";

import {
  faArrowLeft,
  faCalendar,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  addDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfToday,
  format,
  isEqual,
  startOfDay,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
} from "date-fns";
import { DateRange, DayPicker, OnSelectHandler } from "react-day-picker";
import { twJoin, twMerge } from "tailwind-merge";

import { CustomPopover as Popover } from "src/base-components/Popover";
import { DropdownItem } from "src/design-system/Dropdown";
import { Icon, IconButton } from "src/design-system/Icon";
import { StrictDateRange } from "src/utils/timeWindow";
import { assertUnreachable } from "src/utils/typeUtils";

export type { DateRange } from "react-day-picker";

const NUMBER_OF_MONTHS = 2;

export enum PredefinedDateRange {
  LAST_7_DAYS = "last7Days",
  LAST_30_DAYS = "last30Days",
  LAST_COMPLETED_MONTH = "lastCompletedMonth",
  YESTERDAY = "yesterday",
  LAST_24_HOURS = "last24Hours",
  LAST_100_DAYS = "last100Days",
}

export enum SpecificDateOption {
  SPECIFIC_DATES = "specificDates",
}

export type DateRangeOption = PredefinedDateRange | SpecificDateOption;

const getPredefinedDateRange = (
  option: Exclude<DateRangeOption, SpecificDateOption.SPECIFIC_DATES>,
): StrictDateRange => {
  const today = new Date();

  switch (option) {
    case PredefinedDateRange.LAST_7_DAYS:
      return {
        from: startOfDay(subDays(today, 7)),
        to: endOfDay(today),
      };
    case PredefinedDateRange.LAST_30_DAYS:
      return {
        from: startOfDay(subDays(today, 30)),
        to: endOfDay(today),
      };
    case PredefinedDateRange.LAST_COMPLETED_MONTH: {
      const lastMonth = subMonths(today, 1);
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth),
      };
    }
    case PredefinedDateRange.YESTERDAY: {
      const yesterday = subDays(today, 1);
      return {
        from: startOfDay(yesterday),
        to: endOfDay(yesterday),
      };
    }
    case PredefinedDateRange.LAST_100_DAYS: {
      return {
        from: startOfDay(subDays(today, 100)),
        to: endOfDay(today),
      };
    }
    case PredefinedDateRange.LAST_24_HOURS: {
      return {
        from: subHours(today, 24),
        to: today,
      };
    }
    default:
      return assertUnreachable(option);
  }
};

const getPredefinedDateRangeLabel = (option: DateRangeOption): string => {
  switch (option) {
    case PredefinedDateRange.LAST_24_HOURS:
      return "Last 24 hours";
    case PredefinedDateRange.LAST_7_DAYS:
      return "Last 7 days";
    case PredefinedDateRange.LAST_30_DAYS:
      return "Last 30 days";
    case PredefinedDateRange.LAST_100_DAYS:
      return "Last 100 days";
    case PredefinedDateRange.LAST_COMPLETED_MONTH:
      return "Last completed month";
    case PredefinedDateRange.YESTERDAY:
      return "Yesterday";
    case SpecificDateOption.SPECIFIC_DATES:
      return "Specific dates";
    default:
      return assertUnreachable(option);
  }
};

export type DateRangePickerResetable =
  | {
      resetable?: false;
      onReset?: never;
    }
  | {
      resetable: true;
      onReset: () => void;
    };

export type DateRangePickerProps = {
  value: DateRange | undefined;
  onChange: OnSelectHandler<DateRange | undefined>;
  placeholder?: string;
  rangeLimitInDays?: number; // Maximum number of nights between from and to dates (e.g., 30 allows a 31-day range inclusive)
  minRangeLimitInDays?: number;
  borderless?: boolean;
  suffixIcon?: boolean;
  disabled?: boolean;
  disabledBefore?: Date;
  dataLoc?: string;
  buttonRenderer?: (props: {
    value: DateRange | undefined;
    open: boolean;
  }) => React.ReactElement;
  footerRenderer?: () => React.ReactElement;
  usePredefinedDates?: boolean;
  predefinedDateOptions?: PredefinedDateRange[];
  hidePredefinedDateRange?: boolean;
} & DateRangePickerResetable;

type IsDateDisabledParams = {
  day: Date;
  disabledBefore?: Date;
  rangeLimitInDays?: number;
  value?: DateRange;
};

export const isDateDisabled = ({
  day,
  disabledBefore,
  rangeLimitInDays,
  value,
}: IsDateDisabledParams): boolean => {
  if (day > endOfToday()) return true;
  if (disabledBefore && day < disabledBefore) return true;

  if (rangeLimitInDays && value?.from) {
    const isSelectingSecondDate = !value?.to || isEqual(value.from, value.to);
    if (isSelectingSecondDate) {
      // Single date selected - grey out dates outside range from that date
      const minDate = subDays(value.from, rangeLimitInDays);
      const maxDate = addDays(value.from, rangeLimitInDays);
      if (day < minDate || day > maxDate) return true;
    } else if (value?.to) {
      // Both dates selected - grey out dates that would exceed the range limit
      // Available range = rangeLimitInDays - currentRangeLength
      const currentRangeDays = differenceInDays(value.to, value.from);
      const availableDays = rangeLimitInDays - currentRangeDays;
      const minDate = subDays(value.from, availableDays);
      const maxDate = addDays(value.to, availableDays);
      if (day < minDate || day > maxDate) return true;
    }
  }

  return false;
};

const findPredefinedDateOption = (
  value: DateRange | undefined,
  predefinedDateOptions: PredefinedDateRange[],
) => {
  if (!value?.from || !value?.to) return null;

  const { from, to } = value;

  const option = predefinedDateOptions.find((predefinedOption) => {
    const predefinedValue = getPredefinedDateRange(predefinedOption);

    return (
      isEqual(predefinedValue.from, from) && isEqual(predefinedValue.to, to)
    );
  });

  return option ?? null;
};

export const DateRangePicker = ({
  value,
  onChange,
  borderless = false,
  disabled = false,
  suffixIcon = false,
  placeholder = "Pick a date range",
  resetable = false,
  onReset,
  rangeLimitInDays,
  minRangeLimitInDays,
  buttonRenderer,
  footerRenderer,
  usePredefinedDates = false,
  predefinedDateOptions = [
    PredefinedDateRange.LAST_7_DAYS,
    PredefinedDateRange.LAST_30_DAYS,
    PredefinedDateRange.YESTERDAY,
  ],
  hidePredefinedDateRange = false,
  disabledBefore,
  dataLoc = "date-range-picker",
}: DateRangePickerProps) => {
  const [showCalendar, setShowCalendar] = useState(() => {
    if (!value?.from || !value?.to) return false;

    return findPredefinedDateOption(value, predefinedDateOptions) === null;
  });
  const [selectedPredefinedOption, setSelectedPredefinedOption] =
    useState<PredefinedDateRange | null>(() => {
      return findPredefinedDateOption(value, predefinedDateOptions);
    });

  const handlePredefinedOptionSelect = (
    option: DateRangeOption,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => {
    if (option === SpecificDateOption.SPECIFIC_DATES) {
      setShowCalendar(true);
    } else {
      const dateRange = getPredefinedDateRange(option);
      const today = new Date();
      onChange(dateRange, today, {}, event);
      setSelectedPredefinedOption(option);
    }
  };

  const handleCalendarSelect: OnSelectHandler<DateRange | undefined> = (
    range,
    selectedDay,
    modifiers,
    e,
  ) => {
    if (range?.from && range?.to) {
      setSelectedPredefinedOption(null);
    }
    const normalizedRange: DateRange | undefined = range
      ? {
          from: range.from ? startOfDay(range.from) : undefined,
          to: range.to ? endOfDay(range.to) : undefined,
        }
      : range;
    onChange(normalizedRange, selectedDay, modifiers, e);
  };

  const getDisplayContent = () => {
    let dateRangeDisplay;

    if (value?.from) {
      if (value.to) {
        dateRangeDisplay = (
          <>
            {format(value.from, "LLL dd, y")} - {format(value.to, "LLL dd, y")}
          </>
        );
      } else {
        dateRangeDisplay = format(value.from, "LLL dd, y");
      }
    } else {
      dateRangeDisplay = <span className="text-gray-500">{placeholder}</span>;
    }

    if (selectedPredefinedOption && usePredefinedDates) {
      return (
        <span className="font-inter-normal-12px">
          <span className="text-gray-800">
            {getPredefinedDateRangeLabel(selectedPredefinedOption)}
          </span>
          {!hidePredefinedDateRange && (
            <span className="ml-1 text-gray-500">{dateRangeDisplay}</span>
          )}
        </span>
      );
    }

    return dateRangeDisplay;
  };

  return (
    <div className={twJoin("w-full", disabled && "pointer-events-none")}>
      <Popover
        button={
          buttonRenderer ? (
            (props: { open: boolean }) => buttonRenderer({ value, ...props })
          ) : (
            <div
              className={twJoin(
                "flex h-8 w-full items-center gap-x-1 whitespace-nowrap rounded-lg bg-white px-2 py-1 text-left font-inter-normal-12px text-gray-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
                !borderless && "border border-gray-200",
              )}
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (usePredefinedDates) {
                  setShowCalendar(false);
                }
              }}
            >
              <Icon
                variant={disabled ? "disabled" : "default"}
                icon={faCalendar}
                size="xs"
              />
              <div className={twJoin(disabled && "text-gray-400")}>
                {getDisplayContent()}
              </div>
              {(suffixIcon || resetable) && (
                <div className="ml-auto flex items-center">
                  {!disabled && resetable && (value?.to || value?.from) && (
                    <IconButton
                      icon={faXmark}
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReset?.();
                        setSelectedPredefinedOption(null);
                      }}
                    />
                  )}
                  {suffixIcon && <Icon icon={faChevronDown} size="xs" />}
                </div>
              )}
            </div>
          )
        }
        dataLoc={dataLoc}
        placement="bottom"
      >
        {!showCalendar && usePredefinedDates ? (
          <div className="min-w-[267px]">
            {predefinedDateOptions.map((option) => (
              <DropdownItem
                key={option}
                onClick={(e) => handlePredefinedOptionSelect(option, e)}
              >
                <span>{getPredefinedDateRangeLabel(option)}</span>
                {selectedPredefinedOption === option && (
                  <Icon icon={faCheck} size="sm" variant="active" />
                )}
              </DropdownItem>
            ))}

            <div className="my-2.5 border-gray-200 border-t"></div>

            <DropdownItem
              onClick={(e) =>
                handlePredefinedOptionSelect(
                  SpecificDateOption.SPECIFIC_DATES,
                  e,
                )
              }
            >
              <span>
                {getPredefinedDateRangeLabel(SpecificDateOption.SPECIFIC_DATES)}
              </span>
              {!selectedPredefinedOption && value?.from && value?.to && (
                <Icon icon={faCheck} size="sm" variant="active" />
              )}
            </DropdownItem>
            {footerRenderer && footerRenderer()}
          </div>
        ) : (
          <>
            {usePredefinedDates && (
              <div className="flex items-center gap-x-2 px-4 pt-4.5 pb-2.5">
                <IconButton
                  icon={faArrowLeft}
                  size="sm"
                  onClick={() => setShowCalendar(false)}
                />
                <span className="font-inter-semibold-13px">Specific dates</span>
              </div>
            )}
            <Calendar
              defaultMonth={subMonths(
                value?.to || new Date(),
                NUMBER_OF_MONTHS - 1,
              )}
              disabled={(day) =>
                isDateDisabled({
                  day,
                  disabledBefore,
                  rangeLimitInDays,
                  value,
                })
              }
              max={rangeLimitInDays}
              min={minRangeLimitInDays}
              mode="range"
              numberOfMonths={NUMBER_OF_MONTHS}
              selected={value}
              autoFocus
              onSelect={handleCalendarSelect}
            />
            {footerRenderer && footerRenderer()}
          </>
        )}
      </Popover>
    </div>
  );
};

type CalendarProps = React.ComponentProps<typeof DayPicker>;
export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  components,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      className={twMerge("p-3", className)}
      classNames={{
        months: "flex gap-x-4 space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "font-inter-medium-13px text-gray-700 select-none",
        nav: "absolute flex items-center justify-between left-0 right-0 px-4 z-10",
        button_previous: "h-7 w-5 bg-transparent p-0",
        button_next: "h-7 w-5 bg-transparent p-0",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "rounded-md w-9 font-inter-semibold-13px text-gray-700 select-none",
        week: "flex w-full mt-2 rounded-md overflow-hidden",
        day: twJoin(
          "relative p-0 text-center font-inter-normal-13px text-gray-700 focus-within:relative focus-within:z-20",
          "[&:hover:not([aria-selected])]:enabled:rounded-md [&:hover:not([aria-selected])]:enabled:bg-indigo-50 [&:hover:not([aria-selected])]:enabled:text-indigo-500 enabled:[&:not([aria-selected])]:text-gray-700",
        ),
        day_button: "h-9 w-9 p-0",
        selected: twJoin(
          "bg-indigo-500 text-white hover:bg-indigo-500 hover:text-white focus:bg-indigo-500 focus:text-white",
          props.mode === "single" && "rounded-md",
        ),
        range_start: "rounded-l-md",
        range_end: "rounded-r-md",
        outside:
          "enabled:[aria-selected]:[&>button]:!text-indigo-500 enabled:[&:not([aria-selected])]:[&>button]:text-gray-400",
        range_middle: "!bg-indigo-50 !text-indigo-500",
        today:
          "[&:not([aria-selected]):not([data-disabled])]:font-inter-semibold-13px [&:not([aria-selected]):not([data-disabled])]:!text-indigo-500 [&[data-disabled]]:!text-indigo-200",
        disabled: "!text-gray-300",
        ...classNames,
      }}
      components={{
        Chevron: (props) => {
          if (props.orientation === "left") {
            return (
              <Icon
                icon={faChevronLeft}
                variant={props.disabled ? "disabled" : "default"}
                size="xs"
              />
            );
          }
          return (
            <Icon
              icon={faChevronRight}
              variant={props.disabled ? "disabled" : "default"}
              size="xs"
            />
          );
        },
        ...components,
      }}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  );
};
